import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiError } from '../../Documents/format';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT, DESC_FIRST } from '../constants';
import { filtersToParams, isCancelled, journalApi } from '../journalApi';
import { JournalFilters, JournalListResponse, SortField, SortState } from '../types';

/**
 * Таблица журнала: сортировка + пагинация поверх общих фильтров.
 *  - смена фильтров возвращает на первую страницу без лишнего запроса;
 *  - в журнал действий пишется только новый набор фильтров: страница, сортировка и «Обновить» идут с silent;
 *  - при перезагрузке прежние строки остаются на экране.
 */
export function useJournalList(filters: JournalFilters, enabled: boolean, reloadToken: number) {
  const key = useMemo(() => JSON.stringify(filtersToParams(filters)), [filters]);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pageState, setPageState] = useState({ key, page: 1 });
  const [limit, setLimitState] = useState(DEFAULT_PAGE_SIZE);
  const [retryToken, setRetryToken] = useState(0);

  const [data, setData] = useState<JournalListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const audited = useRef<string | null>(null);
  /** Запрос, строки которого уже на экране: возврат на вкладку без изменений не перезапрашивает список */
  const loaded = useRef<string | null>(null);

  const page = pageState.key === key ? pageState.page : 1;

  useEffect(() => {
    if (!enabled) return undefined;
    const signature = [key, sort.sortBy, sort.sortOrder, page, limit, reloadToken, retryToken].join('#');
    if (loaded.current === signature) return undefined;
    const current = ++seq.current;
    const controller = new AbortController();
    const silent = audited.current === key;
    setLoading(true);

    journalApi
      .list(filters, sort, page, limit, silent, controller.signal)
      .then(res => {
        if (current !== seq.current) return;
        // отмечается только после ответа: отменённый запрос (в т.ч. двойной запуск эффекта в dev) просмотр не «съедает»
        audited.current = key;
        loaded.current = signature;
        setData(res);
        setError(null);
      })
      .catch(err => {
        if (isCancelled(err) || current !== seq.current) return;
        setError(apiError(err, 'Не удалось загрузить записи журнала'));
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });

    return () => controller.abort();
    // filters входит в key: сам объект в зависимостях не нужен
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, sort, page, limit, reloadToken, retryToken]);

  const setPage = useCallback((value: number) => setPageState({ key, page: value }), [key]);

  const toggleSort = useCallback((field: SortField) => {
    setSort(prev => (prev.sortBy === field
      ? { sortBy: field, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
      : { sortBy: field, sortOrder: DESC_FIRST.indexOf(field) !== -1 ? 'desc' : 'asc' }));
    setPageState({ key, page: 1 });
  }, [key]);

  const setLimit = useCallback((value: number) => {
    setLimitState(value);
    setPageState({ key, page: 1 });
  }, [key]);

  const retry = useCallback(() => setRetryToken(t => t + 1), []);

  return {
    sort, toggleSort, page, setPage, limit, setLimit,
    rows: data?.entries ?? [],
    pagination: data?.pagination ?? { page: 1, limit, total: 0, totalPages: 1 },
    totals: data?.totals ?? null,
    today: data?.today ?? '',
    loading, error, retry
  };
}
